"use client";

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { isChunkLoadError, shouldReloadForChunkError } from "@/lib/chunkError";
import { UpdateToast } from "../UpdateToast/UpdateToast";

// Brief delay so the "updating…" toast is visible before the reload.
const RELOAD_DELAY_MS = 1200;

/**
 * Recovers from post-deploy version skew for *unhandled* chunk failures. When a
 * tab open across a deploy tries to load a stale JS/CSS chunk the new
 * deployment no longer matches, the import fails — we surface a brief
 * "updating…" toast and do a one-time full reload onto the current version.
 *
 * Errors caught by React (rendered through an error boundary) are handled in
 * `app/[locale]/error.tsx` instead — both share `@/lib/chunkError`.
 */
export default function ChunkReloadGuard() {
  const [reloading, setReloading] = useState(false);
  useEffect(() => {
    const triggerReload = () => {
      if (!shouldReloadForChunkError()) return;
      Sentry.captureMessage("Auto-reloaded after chunk load error (deploy version skew)", {
        level: "warning"
      });
      setReloading(true);
      window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    };
    const onError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error) || isChunkLoadError({
        message: event.message
      })) {
        triggerReload();
      }
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) triggerReload();
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return reloading ? <UpdateToast data-sentry-element="UpdateToast" data-sentry-component="ChunkReloadGuard" data-sentry-source-file="ChunkReloadGuard.tsx" /> : null;
}